import { useState } from "react";
import { X, Users, AlertTriangle, MapPin, Shield, Calendar } from "lucide-react";
import SectionCard from "./SectionCard";
import DonutChart from "./charts/DonutChart";
import BarChart from "./charts/BarChart";
import { formatNum } from "@/lib/constants";
import { cn } from "@/lib/utils";

export default function CohorteDetailPanel({ etapa, desglose, etapaData, onClose }) {
  const [planTab, setPlanTab] = useState("isapre");

  if (!etapa) return null;

  const planData = desglose ? (planTab === "isapre" ? desglose.planIsapre : desglose.planUC) : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cohorte-detail-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white"
                   style={{ backgroundColor: etapaData?.color || "#7C3AED" }}>
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{etapa.replace(/_/g, " ")}</h3>
                <p className="text-xs text-slate-500">Desglose de la cohorte</p>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
                    aria-label="Cerrar panel">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* KPIs de la etapa */}
          {etapaData && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pacientes</div>
                <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(etapaData.pacientes)}</div>
                <div className="text-xs text-slate-500 tabular-nums">{etapaData.pctTotal}% del total</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Detenidos 30d</div>
                <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: etapaData.alerta ? "#EF4444" : "#64748B" }}>
                  {formatNum(etapaData.detenidos)}
                </div>
                <div className={`text-xs font-semibold tabular-nums ${etapaData.alerta ? "text-rose-600" : "text-slate-500"}`}>
                  {etapaData.pctDetenidos}% detenidos
                  {etapaData.alerta && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                </div>
              </div>
            </div>
          )}

          {!desglose ? (
            <div className="text-center py-8 text-sm text-slate-400">Desglose no disponible para esta etapa.</div>
          ) : (
            <>
              {/* Previsión */}
              <SectionCard title="Por previsión" subtitle="Distribución estimada">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                  <DonutChart
                    labels={desglose.prevision.labels}
                    series={desglose.prevision.series}
                    colors={desglose.prevision.colors}
                    height={220}
                  />
                  <div className="space-y-1.5">
                    {desglose.prevision.labels.map((label, i) => (
                      <div key={label} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: desglose.prevision.colors?.[i] || "#7C3AED" }} />
                          <span className="text-xs text-slate-700">{label}</span>
                        </div>
                        <span className="text-xs font-semibold text-slate-900 tabular-nums">{formatNum(desglose.prevision.series[i])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </SectionCard>

              {/* Plan con tabs */}
              <SectionCard title="Por plan" subtitle="ISAPRE o Plan UC">
                <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-4 w-fit">
                  <button
                    onClick={() => setPlanTab("isapre")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium transition",
                      planTab === "isapre" ? "bg-violet-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    ISAPRE
                  </button>
                  <button
                    onClick={() => setPlanTab("uc")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium transition",
                      planTab === "uc" ? "bg-violet-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    Plan UC
                  </button>
                </div>
                {planData && planData.labels.length > 0 ? (
                  <BarChart
                    categories={planData.labels}
                    series={[{ name: "Pacientes", data: planData.series }]}
                    colors={["#8B5CF6"]}
                    height={220}
                    horizontal
                  />
                ) : (
                  <p className="text-sm text-slate-400 py-6 text-center">Sin datos de plan</p>
                )}
              </SectionCard>

              {/* Edad */}
              <SectionCard title="Por rango de edad" subtitle="Distribución estimada">
                <BarChart
                  categories={desglose.edad.labels}
                  series={[{ name: "Pacientes", data: desglose.edad.series }]}
                  colors={["#3B82F6"]}
                  height={220}
                />
              </SectionCard>

              {/* Centro */}
              <SectionCard title="Por centro" subtitle="Distribución estimada">
                <BarChart
                  categories={desglose.centro.labels}
                  series={[{ name: "Pacientes", data: desglose.centro.series }]}
                  colors={["#06B6D4"]}
                  height={220}
                  horizontal
                />
              </SectionCard>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
